import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import type {
  FilterableEntity,
  JobFilters,
  OptimizationFilters,
  PaginationState,
  RunFilters,
} from "@src/types/filters";
import {
  getDefaultJobFilters,
  getDefaultOptimizationFilters,
  getDefaultRunFilters,
} from "@src/types/filters";
import { entityFiltersActions } from "@src/services/state/entityFiltersSlice";
import type { RootState } from "@src/services/state/store";

// Type map for entity key to filter type
type EntityFilterMap = {
  backtests: RunFilters;
  executions: RunFilters;
  jobs: JobFilters;
  optimizations: OptimizationFilters;
};

// Return type for useEntityFilters
interface UseEntityFiltersReturn<T extends FilterableEntity> {
  filters: EntityFilterMap[T];
  pagination: PaginationState;
  applyFilters: (filters: EntityFilterMap[T]) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  nextPage: () => void;
  prevPage: () => void;
}

/**
 * Hook for managing entity filters and pagination with URL + Redux sync.
 *
 * Architecture:
 * - URL is source of truth (prepared for future URL persistence)
 * - Redux provides cross-component access and sync
 * - Filter changes require "Filter" button (applyFilters/clearFilters)
 * - Pagination changes are immediate (setPage/setPerPage/nextPage/prevPage)
 * - Auto-reset on unmount (page leave)
 */
export function useEntityFilters<T extends FilterableEntity>(
  entityKey: T,
): UseEntityFiltersReturn<T> {
  const dispatch = useDispatch();

  // Select current entity state from Redux
  const entityState = useSelector((state: RootState) => state.entityFilters[entityKey]);

  // Filters are stored as-is in Redux (arrays for serializability)
  const filters = entityState.filters as EntityFilterMap[T];

  const pagination = entityState.pagination;

  // Filter actions (require "Filter" button)
  const applyFilters = useCallback(
    (newFilters: EntityFilterMap[T]) => {
      dispatch(
        entityFiltersActions.setFilters({
          entity: entityKey,
          filters: newFilters as RunFilters | JobFilters | OptimizationFilters,
        }),
      );
    },
    [dispatch, entityKey],
  );

  const clearFilters = useCallback(() => {
    const defaults = getEntityDefaults(entityKey);
    dispatch(
      entityFiltersActions.setFilters({
        entity: entityKey,
        filters: defaults.filters,
      }),
    );
  }, [dispatch, entityKey]);

  // Pagination actions (immediate)
  const setPage = useCallback(
    (page: number) => {
      dispatch(
        entityFiltersActions.setPagination({
          entity: entityKey,
          pagination: { page },
        }),
      );
    },
    [dispatch, entityKey],
  );

  const setPerPage = useCallback(
    (perPage: number) => {
      dispatch(
        entityFiltersActions.setPagination({
          entity: entityKey,
          pagination: { perPage, page: 0 }, // Reset to first page
        }),
      );
    },
    [dispatch, entityKey],
  );

  const nextPage = useCallback(() => {
    const newPage = pagination.page + 1;
    dispatch(
      entityFiltersActions.setPagination({
        entity: entityKey,
        pagination: { page: newPage },
      }),
    );
  }, [dispatch, entityKey, pagination.page]);

  const prevPage = useCallback(() => {
    const newPage = Math.max(0, pagination.page - 1);
    dispatch(
      entityFiltersActions.setPagination({
        entity: entityKey,
        pagination: { page: newPage },
      }),
    );
  }, [dispatch, entityKey, pagination.page]);

  // Reset entity on unmount (page leave)
  useEffect(() => {
    return () => {
      dispatch(entityFiltersActions.resetEntity(entityKey));
    };
  }, [dispatch, entityKey]);

  return {
    // Current applied state
    filters,
    pagination,

    // Filter actions (require "Filter" button)
    applyFilters,
    clearFilters,

    // Pagination actions (immediate)
    setPage,
    setPerPage,
    nextPage,
    prevPage,
  };
}

// Helper to get default filters for an entity
function getEntityDefaults(entityKey: FilterableEntity): {
  filters: RunFilters | JobFilters | OptimizationFilters;
} {
  switch (entityKey) {
    case "backtests":
      return { filters: { ...getDefaultRunFilters(), active: false } };
    case "executions":
      return { filters: { ...getDefaultRunFilters(), active: true } };
    case "jobs":
      return { filters: getDefaultJobFilters() };
    case "optimizations":
      return { filters: getDefaultOptimizationFilters() };
  }
}
